import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { hitosAPI } from '../utils/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Flag, Loader2, ToggleLeft, ToggleRight, Copy } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return dateStr; }
};

const Hitos = () => {
  const [hitos, setHitos] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadHitos(); }, []);

  const loadHitos = async () => {
    try {
      const response = await hitosAPI.getAll();
      setHitos(response.data || []);
    } catch {
      toast.error('Error al cargar hitos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Está seguro de eliminar este hito?')) return;
    try {
      await hitosAPI.delete(id);
      toast.success('Hito eliminado');
      loadHitos();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleToggle = async (e, id) => {
    e.stopPropagation();
    try {
      const resp = await hitosAPI.toggle(id);
      toast.success(resp.data.activo ? 'Hito activado' : 'Hito desactivado');
      loadHitos();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const handleDuplicar = async (e, id, codigo) => {
    e.stopPropagation();
    try {
      await hitosAPI.duplicar(id);
      toast.success(`Hito ${codigo} duplicado`);
      loadHitos();
    } catch { toast.error('Error al duplicar'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 size={36} className="animate-spin text-[#9d8272] mx-auto" />
          <p className="mt-4 text-sm text-[#5A5A5A]">Cargando hitos...</p>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Hitos
            </h1>
            <p className="text-sm sm:text-lg text-[#5A5A5A] mt-1">Gestiona los hitos de proyectos</p>
          </div>
          <Button
            onClick={() => navigate('/hitos/nuevo')}
            className="bg-[#9d8272] hover:bg-[#8a7162] text-white self-start sm:self-auto"
            data-testid="nuevo-hito-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Hito
          </Button>
        </div>

        {hitos.length === 0 ? (
          <div className="bg-white border border-[#E8E8E7] rounded-lg p-8 text-center">
            <Flag size={48} className="mx-auto text-[#c5b8a2] mb-4" />
            <p className="text-[#5A5A5A]">No hay hitos registrados</p>
            <Button onClick={() => navigate('/hitos/nuevo')} className="mt-4 bg-[#9d8272] hover:bg-[#8a7162] text-white">
              Crear primer hito
            </Button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white border border-[#E8E8E7] shadow-sm rounded-lg" data-testid="hitos-table">
              <div className="px-6 py-4 border-b border-[#E8E8E7]">
                <h2 className="text-lg font-semibold text-[#2D2D2D]">Listado de Hitos</h2>
              </div>
              <div className="px-6 pb-4 pt-2 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E8E8E7] text-left text-sm">
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Código</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Nombre</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Proyectos</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Subdivisiones</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Fecha</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A]">Estado</th>
                    <th className="pb-2 font-semibold text-[#5A5A5A] text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {hitos.map((hito) => (
                    <tr
                      key={hito.id}
                      onClick={() => navigate(`/hitos/${hito.id}`)}
                      className="border-b border-[#E8E8E7] hover:bg-[#F5F5F5] transition-colors text-sm h-12 cursor-pointer"
                      data-testid={`hito-row-${hito.id}`}
                    >
                      <td className="py-2 text-[#9d8272] font-bold align-middle">{hito.codigo}</td>
                      <td className="py-2 text-[#2D2D2D] font-medium align-middle">
                        <div className="truncate max-w-[200px]" title={hito.nombre}>{hito.nombre}</div>
                      </td>
                      <td className="py-2 align-middle">
                        <ProyectosCell items={hito.proyectos_info || []} />
                      </td>
                      <td className="py-2 align-middle">
                        <SubdivisionesCell items={hito.subdivisiones_info || []} />
                      </td>
                      <td className="py-2 text-[#888888] text-xs align-middle whitespace-nowrap">{formatDate(hito.created_at)}</td>
                      <td className="py-2 align-middle">
                        <EstadoBadge activo={hito.activo} />
                      </td>
                      <td className="py-2 align-middle">
                        <div className="flex items-center justify-center gap-1">
                          <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); navigate(`/hitos/${hito.id}`); }} data-testid={`editar-hito-${hito.id}`}>
                            <Edit size={14} className="text-[#9d8272]" />
                          </Button>
                          </TooltipTrigger><TooltipContent>Editar hito</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => handleDuplicar(e, hito.id, hito.codigo)} data-testid={`duplicar-hito-${hito.id}`}>
                            <Copy size={14} className="text-[#9d8272]" />
                          </Button>
                          </TooltipTrigger><TooltipContent>Duplicar hito</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => handleToggle(e, hito.id)} data-testid={`toggle-hito-${hito.id}`}>
                            {hito.activo ? <ToggleRight size={16} className="text-green-600" /> : <ToggleLeft size={16} className="text-[#888888]" />}
                          </Button>
                          </TooltipTrigger><TooltipContent>{hito.activo ? 'Desactivar' : 'Activar'}</TooltipContent></Tooltip>
                          <Tooltip><TooltipTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => handleDelete(e, hito.id)} data-testid={`eliminar-hito-${hito.id}`}>
                            <Trash2 size={14} />
                          </Button>
                          </TooltipTrigger><TooltipContent>Eliminar hito</TooltipContent></Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3" data-testid="hitos-cards">
              {hitos.map((hito) => (
                <div
                  key={hito.id}
                  onClick={() => navigate(`/hitos/${hito.id}`)}
                  className="bg-white border border-[#E8E8E7] rounded-lg p-4 cursor-pointer hover:shadow-sm transition-shadow"
                  data-testid={`hito-card-${hito.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-[#2D2D2D]">{hito.nombre}</span>
                        <Badge variant="outline" className="text-xs font-mono">{hito.codigo}</Badge>
                        <EstadoBadge activo={hito.activo} />
                      </div>
                      <p className="text-xs text-[#888888] mt-1">{formatDate(hito.created_at)}</p>
                    </div>
                  </div>
                  {(hito.proyectos_info || []).length > 0 && (
                    <div className="text-xs text-[#5A5A5A] mt-1">
                      <span className="font-medium">Proyectos: </span>
                      {hito.proyectos_info.map(p => p.codigo || p.nombre).join(', ')}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-[#F5F5F5]">
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/hitos/${hito.id}`); }}>
                      <Edit size={12} className="mr-1" /> Editar
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => handleDuplicar(e, hito.id, hito.codigo)}>
                      <Copy size={12} className="mr-1" /> Duplicar
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => handleToggle(e, hito.id)}>
                      {hito.activo ? <ToggleRight size={14} className="text-green-600" /> : <ToggleLeft size={14} />}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500" onClick={(e) => handleDelete(e, hito.id)}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

// ---- Helper Components ----
const EstadoBadge = ({ activo }) => (
  <Badge className={`text-[10px] ${activo ? 'bg-green-100 text-green-700 hover:bg-green-100' : 'bg-red-100 text-red-700 hover:bg-red-100'}`}>
    {activo ? 'Activo' : 'Inactivo'}
  </Badge>
);

const ProyectosCell = ({ items }) => {
  if (!items.length) return <span className="text-xs text-[#888888]">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((p, i) => (
        <Tooltip key={i}>
          <TooltipTrigger asChild>
            <span className="text-xs bg-[#f0ece6] text-[#5a4a3a] px-1.5 py-0.5 rounded cursor-default">
              {p.codigo || '?'}
            </span>
          </TooltipTrigger>
          <TooltipContent><p>{p.nombre}</p></TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
};

const SubdivisionesCell = ({ items }) => {
  if (!items.length) return <span className="text-xs text-[#888888]">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((s, i) => (
        <Tooltip key={i}>
          <TooltipTrigger asChild>
            <span className="text-xs bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded cursor-default">
              {s.codigo || '?'}
            </span>
          </TooltipTrigger>
          <TooltipContent><p>{s.nombre}</p></TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
};

export default Hitos;
